import { formatCompanyAddress } from '@fos/shared/contracts/company-registry';
import type { ClientDto } from '@fos/shared/contracts/crm';
import { CLIENT_COLOR_LABELS, COMPANY_VAT_STATUS_LABELS, COMPANY_VAT_STATUS_TONES } from '@fos/shared/labels';
import { formatNip } from '@fos/shared/nip';
import { Archive, ArchiveRestore, Pencil, UserRoundSearch } from 'lucide-react';
import { type ReactNode, useState } from 'react';
import { Link, useParams } from 'react-router';
import { appErrorCode } from '../../../lib/api';
import { trpc } from '../../../lib/trpc';
import { useAccess } from '../../../lib/useAccess';
import { Button } from '../../../ui/Button';
import { Markdown } from '../../../ui/Markdown';
import { Mono } from '../../../ui/Mono';
import { Page } from '../../../ui/Page';
import { PageHeader, SectionHeader } from '../../../ui/PageHeader';
import { Section } from '../../../ui/Section';
import { EmptyState, ErrorState, ListSkeleton } from '../../../ui/States';
import { Status } from '../../../ui/Status';
import { TOUCH_BUTTON } from '../../mobile';
import { ModulePage } from '../../modules/pages/ModulePage';
import { clientStatusLabel, formatClientRate, websiteLabel } from '../client-format';
import { ArchiveClientDialog } from '../components/ArchiveClientDialog';
import { ClientDot } from '../components/ClientDot';
import { ClientFormDialog } from '../components/ClientFormDialog';
import { registrySourceLabel } from '../components/CompanyLookup';
import { ContactConflictList } from '../components/ContactConflictList';

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="grid grid-cols-[140px_1fr] gap-4 border-b border-line py-3 text-body-s">
      <dt className="text-fg-muted">{label}</dt>
      <dd className="min-w-0 text-fg">{children ?? <span className="text-fg-muted">–</span>}</dd>
    </div>
  );
}

/**
 * Ustawienia klienta (/klienci/:id/ustawienia): dane klienta z edycją, konflikty emaili kontaktów z akcjami
 * rozstrzygnięcia, archiwizacja. Widok bez projektu w Figmie (wzorce: Ustawienia, listy) — patrz
 * IMPLEMENTATION_STATUS „czeka na projekt”.
 */
export function ClientSettingsPage() {
  const access = useAccess();
  if (!access.can('clients')) return <ModulePage module="clients" />;
  return <ClientSettings />;
}

function ClientSettings() {
  const { id = '' } = useParams();
  const query = trpc.clients.get.useQuery(
    { id },
    { enabled: Boolean(id), retry: (n, err) => n < 2 && appErrorCode(err) !== 'NOT_FOUND' },
  );

  if (query.isPending) {
    return (
      <Page width="reading">
        <ListSkeleton rows={6} />
      </Page>
    );
  }
  if (query.isError || query.data.partial) {
    const notFound = query.isError ? appErrorCode(query.error) === 'NOT_FOUND' : true;
    return (
      <Page width="reading">
        <PageHeader breadcrumbs={[{ label: 'Klienci', to: '/klienci' }]} title="Ustawienia klienta" />
        <Section className="mt-6">
          {notFound ? (
            <EmptyState
              icon={UserRoundSearch}
              title="Nie znaleziono klienta"
              description="Klient nie istnieje albo nie masz dostępu do jego ustawień."
              action={
                <Button asChild variant="secondary" className={TOUCH_BUTTON}>
                  <Link to="/klienci">Wróć do klientów</Link>
                </Button>
              }
            />
          ) : (
            <ErrorState onRetry={() => void query.refetch()} retrying={query.isFetching} />
          )}
        </Section>
      </Page>
    );
  }
  return <ClientSettingsView client={query.data} />;
}

function ClientSettingsView({ client }: { client: ClientDto }) {
  const access = useAccess();
  const canEdit = client.canEdit && access.can('clients', 'edit');
  const archived = client.status === 'archived' || client.archivedAt !== null;
  const [editOpen, setEditOpen] = useState(false);
  const [archiveOpen, setArchiveOpen] = useState(false);

  return (
    <Page width="reading">
      <PageHeader
        breadcrumbs={[
          { label: 'Klienci', to: '/klienci' },
          { label: client.name, to: `/klienci/${client.id}` },
        ]}
        title="Ustawienia klienta"
        meta={<span className="text-fg-muted">{client.name}</span>}
      />

      <Section className="mt-6" aria-labelledby="client-data">
        <SectionHeader
          title={<span id="client-data">Dane klienta</span>}
          tone="strong"
          action={
            canEdit && !archived ? (
              <Button variant="ghost" size="s" icon={Pencil} onClick={() => setEditOpen(true)} className={TOUCH_BUTTON}>
                Edytuj
              </Button>
            ) : undefined
          }
        />
        <dl className="border-t border-line">
          <Row label="Nazwa">{client.name}</Row>
          <Row label="Status">{clientStatusLabel(client.status)}</Row>
          {'defaultRate' in client && (
            <Row label="Stawka domyślna">
              {client.defaultRate ? (
                <>
                  <Mono>{formatClientRate(client.defaultRate, client.currency)}</Mono> netto
                </>
              ) : null}
            </Row>
          )}
          <Row label="Kolor">
            {client.color ? (
              <span className="inline-flex items-center gap-2">
                <ClientDot color={client.color} size={10} />
                {CLIENT_COLOR_LABELS[client.color] ?? <Mono>{client.color}</Mono>}
              </span>
            ) : null}
          </Row>
          <Row label="Opiekun">{client.owner?.name ?? null}</Row>
          <Row label="Nazwa prawna">{client.company.legalName ?? null}</Row>
          <Row label="NIP">{client.company.nip ? <Mono>{formatNip(client.company.nip)}</Mono> : null}</Row>
          {(client.company.regon || client.company.krs) && (
            <Row label="REGON / KRS">
              <Mono>{client.company.regon ?? '–'}</Mono>
              <span className="text-fg-muted"> · </span>
              <Mono>{client.company.krs ?? '–'}</Mono>
            </Row>
          )}
          {client.company.vatStatus && (
            <Row label="Status VAT">
              <Status tone={COMPANY_VAT_STATUS_TONES[client.company.vatStatus]}>
                {COMPANY_VAT_STATUS_LABELS[client.company.vatStatus]}
              </Status>
            </Row>
          )}
          <Row label="Adres">{formatCompanyAddress(client.company.address) || null}</Row>
          {client.company.registrySource && (
            <Row label="Dane firmy z">
              <span className="text-fg-secondary">
                {registrySourceLabel(client.company.registrySource, client.company.registryCheckedAt)}
              </span>
            </Row>
          )}
          <Row label="Strona">
            {client.company.website ? (
              <a
                href={client.company.website}
                target="_blank"
                rel="noreferrer noopener"
                className="rounded-xs text-accent hover:underline focus-visible:outline-2 focus-visible:outline-accent"
              >
                {websiteLabel(client.company.website)}
              </a>
            ) : null}
          </Row>
          <Row label="Notatki">{client.notes ? <Markdown className="text-body-s">{client.notes}</Markdown> : null}</Row>
          <Row label="Odnośniki">
            {client.links.length > 0 ? (
              <ul className="flex flex-col gap-1">
                {client.links.map((l) => (
                  <li key={`${l.label}|${l.url}`}>
                    <a
                      href={l.url}
                      target="_blank"
                      rel="noreferrer noopener"
                      className="rounded-xs text-accent hover:underline focus-visible:outline-2 focus-visible:outline-accent"
                    >
                      {l.label}
                    </a>
                  </li>
                ))}
              </ul>
            ) : null}
          </Row>
        </dl>
      </Section>

      <Section className="mt-4 md:mt-6" aria-labelledby="client-conflicts">
        <SectionHeader title={<span id="client-conflicts">Konflikty kontaktów</span>} tone="strong" />
        <p className="mb-3 text-caption text-fg-muted">
          Ten sam email u kilku klientów. Rozstrzygnij, czy to ta sama osoba — od tego zależy przypisanie poczty do
          klienta.
        </p>
        <ContactConflictList clientId={client.id} />
      </Section>

      {canEdit && (
        <Section className="mt-4 md:mt-6" aria-labelledby="client-archive">
          <SectionHeader title={<span id="client-archive">Archiwum</span>} tone="strong" />
          <div className="flex flex-col gap-3 border-t border-line pt-3 md:flex-row md:items-center">
            <p className="min-w-0 flex-1 text-body-s text-fg-secondary">
              {archived
                ? 'Klient jest w archiwum. Przywrócenie przywraca go na listy i do wyboru.'
                : 'Archiwizacja ukrywa klienta na listach. Historia, kontakty, projekty i zadania zostają.'}
            </p>
            <Button
              variant="secondary"
              icon={archived ? ArchiveRestore : Archive}
              onClick={() => setArchiveOpen(true)}
              className={TOUCH_BUTTON}
            >
              {archived ? 'Przywróć' : 'Przenieś do archiwum'}
            </Button>
          </div>
        </Section>
      )}

      {canEdit && <ClientFormDialog open={editOpen} onOpenChange={setEditOpen} client={client} />}
      {canEdit && (
        <ArchiveClientDialog
          open={archiveOpen}
          onOpenChange={setArchiveOpen}
          client={client}
          mode={archived ? 'restore' : 'archive'}
        />
      )}
    </Page>
  );
}
